<template>
  <img
    :src="currentSrc"
    :alt="alt"
    :style="placeholderStyle"
    :loading="loadingAttr"
    :fetchpriority="priorityAttr"
    decoding="async"
    referrerpolicy="no-referrer"
    @error="handleError"
    @load="$emit('load', $event)"
  />
</template>

<script>
import { buildImageUrl, placeholderFor } from '@/utils/image'

/**
 * 本次会话内「已知加载失败的缩略图地址」。
 *
 * 为什么需要它：数据万象的现场处理在 CDN 缓存未命中时会失败，失败的请求根本没完成，
 * 所以那个派生图**永远不会被缓存**，下次再请求还是失败。如果每次组件挂载都老老实实
 * 从第 0 级（缩略图）重试一遍，等于每次打开页面都白付一次失败的往返 —— 表现就是
 * 「个人中心/会话列表的头像每次打开都很慢」（第二次之后原图已在浏览器缓存里，
 * 慢的其实全是那次注定失败的缩略图请求）。
 *
 * 记下来之后，同一次会话里再遇到这个地址就直接用原图，不再重试。
 * 只放在内存里、不持久化：CDN 那边预热好之后刷新页面就会重新尝试缩略图。
 */
const failedThumbs = new Set()

/**
 * 全站统一的图片组件（已全局注册，直接写 <smart-img> 即可）
 *
 * 它把三件事收在一处，避免在几十个渲染点里重复：
 *
 * 1. 走 CDN 缩略图 —— 按用途向数据万象要合适尺寸，而不是拉原图再用 CSS 缩。
 *    头像 176KB → 约 5KB，配图 268KB → 约 14KB。
 *
 * 2. 三级降级 —— 缩略图 →（失败）→ 原图 →（失败）→ 本地内联 SVG 占位。
 *    这一步是必须的：数据万象的现场处理链路在 CDN 缓存未命中时会失败
 *    （详见 @/utils/image 顶部注释），不能让它决定图片能不能显示。
 *    另外历史上有 img.shiyit.com / img.yqingke.com 两个已失效域名散落在数据里，
 *    这类地址第一级就直接失败，也会稳定落到占位图，不会出现裂图。
 *
 * 3. 加载时机 —— 首屏 eager + fetchpriority=high，其余 lazy。
 *    注意不要无脑写 loading="lazy"：列表容器在骨架屏期间是 display:none，
 *    浏览器对隐藏容器里的 lazy 图片不发请求，首屏图会等骨架屏收起后才开始下载。
 *
 * 4. referrerpolicy="no-referrer" —— **不要删**。
 *    COS 存储桶开了防盗链，白名单只放行 *.ylinhua.com 和「空 Referer」；
 *    localhost / 127.0.0.1 / 不带 www 的 ylinhua.com 会被拒，返回 403 + XML 错误体，
 *    <img> 拿到 XML 后被 Chrome ORB 拦掉（net::ERR_BLOCKED_BY_ORB），表现为裂图。
 *    而且只在 CDN 未命中缓存（回源）时才触发，所以是「一部分图裂」而不是全裂。
 *    不带 Referer 就能稳定命中白名单里的「空 Referer」，本地开发也就不再裂图。
 *    index.html 里的 <meta name="referrer" content="same-origin"> 是同一件事的兜底，
 *    这里再显式声明一次，避免有人改动 meta 时把图片一起弄挂。
 *
 * 用法：
 *   <smart-img mode="avatar" :src="user.avatar" />
 *   <smart-img mode="avatar" :src="user.avatar" eager />
 *   <smart-img mode="content" :src="img" :size="720" />
 *   <smart-img mode="cover" :src="img" :size="360" />   // object-fit: cover 的正方形格子
 *
 * ⚠️ 头像请一律用默认尺寸（96），不要传 :size。多一种派生尺寸就多一批「永远冷」的
 *    派生图：实测 96 档位 8/8 命中 CDN 缓存、39~145ms，而只有个人中心在用的 192 档位
 *    0/8 命中、每张 296~455ms。
 *   <smart-img mode="raw" :src="localOrSignedUrl" />   // 不加任何参数，仅保留降级
 */
export default {
  name: 'SmartImg',

  props: {
    /** 原始图片地址（接口返回的、未加处理参数的地址） */
    src: {
      type: String,
      default: ''
    },
    /**
     * avatar  —— 正方形头像，居中裁剪
     * content —— 内容图，限制最长边、只缩不放（按原图比例完整显示的场景）
     * cover   —— 正方形格子（object-fit: cover）里的内容图，短边优先 + 居中裁成正方形；
     *            九宫格这类格子必须用它，用 content 的话长图会被压成细条、显示发糊
     * raw     —— 不加处理参数（本地 blob 预览、签名链接、第三方域名）
     */
    mode: {
      type: String,
      default: 'content',
      validator: v => ['avatar', 'content', 'cover', 'raw'].indexOf(v) !== -1
    },
    /** 目标尺寸（avatar / cover = 边长，content = 最长边）。会在 utils/image 里向上对齐到固定档位 */
    size: {
      type: Number,
      default: 0
    },
    alt: {
      type: String,
      default: ''
    },
    /**
     * 首屏图片传 true：抬高下载优先级（fetchpriority=high），插到大图前面去。
     *
     * 注意它**不再**决定要不要懒加载 —— 头像一律不懒加载，详见 loadingAttr。
     */
    eager: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      // 0 = CDN 缩略图，1 = 原图，2 = 本地占位图
      stage: 0
    }
  },

  computed: {
    normalizedSrc () {
      return (this.src || '').trim()
    },

    /** 第 0 级要用的缩略图地址 */
    thumbSrc () {
      return buildImageUrl(this.normalizedSrc, this.mode, this.size || undefined)
    },

    currentSrc () {
      if (!this.normalizedSrc) return placeholderFor(this.mode)
      if (this.stage === 0) {
        // 这个缩略图本次会话里已经失败过，别再白试一次，直接用原图
        if (failedThumbs.has(this.thumbSrc)) return this.normalizedSrc
        return this.thumbSrc
      }
      if (this.stage === 1) return this.normalizedSrc
      return placeholderFor(this.mode)
    },

    // 头像加载期间先铺一层浅灰，避免出现一个空洞（感知上「更快出来」）。
    // 只对头像做：内容图和 raw（logo 多为透明 PNG）铺灰底会更难看。
    placeholderStyle () {
      return this.mode === 'avatar' ? { backgroundColor: '#f2f3f5' } : null
    },

    /**
     * 头像一律 eager，不做懒加载。
     *
     * 实测推荐页时间线（本地、CDN 全部缓存命中）：
     *   685ms 列表接口返回 → 803~842ms 头像缩略图返回 → 852ms 第一个头像可见
     * 但一页 10 个头像里只有 6 个加载了，剩下 4 个因为 loading="lazy" 要等滚动才发请求。
     * 头像走缩略图之后只有 3~6KB，一整页 10 个也就 50KB 上下 —— 为它做懒加载省下的流量
     * 远远抵不上「滚一下才出来」的体感损失。
     *
     * 内容图不一样（一张十几 KB、一页可能几十张），保持默认懒加载，只有显式传 eager
     * 的首屏图才立即加载。
     */
    loadingAttr () {
      if (this.mode === 'avatar') return 'eager'
      return this.eager ? 'eager' : 'lazy'
    },

    // 优先级仍然按首屏区分：几百个头像都标 high 就等于没有优先级
    priorityAttr () {
      return this.eager ? 'high' : 'auto'
    }
  },

  watch: {
    // 列表复用 DOM / 切 tab 换了新地址，新地址要从第一级重新开始
    normalizedSrc () {
      this.stage = 0
    }
  },

  methods: {
    handleError (e) {
      if (this.stage === 0) {
        // 注意：如果这张图不在 CDN 白名单里（例如已失效的 img.shiyit.com、
        // 或者本地 blob:），buildImageUrl 会把地址原样返回 —— 此时第 0 级和第 1 级
        // 是同一个地址。若照常降到第 1 级，src 属性没有变化，浏览器不会重新发起加载，
        // 也就不会再触发 error，图片会永久卡在裂图状态。所以这种情况直接跳到占位图。
        if (this.thumbSrc === this.normalizedSrc) {
          this.toPlaceholder()
          return
        }
        // 记下这个失败的派生图，本次会话内别再重试（见文件顶部 failedThumbs 的注释）
        failedThumbs.add(this.thumbSrc)
        this.stage = 1
        return
      }
      if (this.stage === 1) {
        this.toPlaceholder()
        return
      }
      // 已经是本地 data URI 了，不会再触发 error，这里只是把事件透出去
      this.$emit('error', e)
    },

    /**
     * 缩略图和原图都失败了，降到本地占位图，并发出 fallback 事件。
     * 需要「加载失败、点击重试」这类交互的调用方（聊天图片）监听它，换成自己的状态；
     * 不监听的调用方行为不变。不复用 error 事件：已有页面监听着 error，语义不一样。
     */
    toPlaceholder () {
      this.stage = 2
      this.$emit('fallback', this.normalizedSrc)
    }
  }
}
</script>
